import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useCurrentUser } from '@/api/auth/queries/useCurrentUser'
import { useUpdateNotificationPreferences } from '@/api/user/mutations/useUpdateNotificationPreferences'
import { DEFAULT_NOTIFICATION_PREFERENCES } from '@/api/user/types/User'
import { Card, CardContent } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { isNativePlatform } from '@/lib/native'

export function NotificationPreferencesCard() {
  const { t } = useTranslation()
  const { data: user } = useCurrentUser()
  const { mutate, isPending } = useUpdateNotificationPreferences()
  const [error, setError] = useState('')

  if (!isNativePlatform() || !user) return null

  const prefs = {
    ...DEFAULT_NOTIFICATION_PREFERENCES,
    ...user.notificationPreferences,
  }

  const toggle = (key: keyof typeof prefs, value: boolean) => {
    setError('')
    mutate(
      { ...prefs, [key]: value },
      {
        onError: () => setError(t('native.preferencesSaveError')),
      }
    )
  }

  return (
    <Card className='bg-white'>
      <CardContent className='space-y-4'>
        <div>
          <h2 className='text-text-primary text-base font-medium'>{t('native.notificationsTitle')}</h2>
          <p className='text-text-secondary text-sm'>{t('native.notificationsSubtitle')}</p>
        </div>
        <PreferenceRow
          id='notifyPkgArrived'
          label={t('native.notifyPkgArrived')}
          checked={prefs.notifyPkgArrived}
          disabled={isPending}
          onChange={value => toggle('notifyPkgArrived', value)}
        />
        <PreferenceRow
          id='notifyPaymentDue'
          label={t('native.notifyPaymentDue')}
          checked={prefs.notifyPaymentDue}
          disabled={isPending}
          onChange={value => toggle('notifyPaymentDue', value)}
        />
        <PreferenceRow
          id='notifyPkgReqAnswered'
          label={t('native.notifyPkgReqAnswered')}
          checked={prefs.notifyPkgReqAnswered}
          disabled={isPending}
          onChange={value => toggle('notifyPkgReqAnswered', value)}
        />
        <PreferenceRow
          id='notifyPickupAssigned'
          label={t('native.notifyPickupAssigned')}
          checked={prefs.notifyPickupAssigned}
          disabled={isPending}
          onChange={value => toggle('notifyPickupAssigned', value)}
        />
        {error ? <p className='text-sm text-red-600'>{error}</p> : null}
      </CardContent>
    </Card>
  )
}

function PreferenceRow({
  id,
  label,
  checked,
  disabled,
  onChange,
}: {
  id: string
  label: string
  checked: boolean
  disabled: boolean
  onChange: (value: boolean) => void
}) {
  return (
    <div className='flex items-center justify-between gap-4'>
      <Label htmlFor={id} className='text-sm font-medium'>
        {label}
      </Label>
      <Switch id={id} checked={checked} disabled={disabled} onCheckedChange={onChange} />
    </div>
  )
}
